"use client";
import DttHeader from "@/components/DttHeader";
import { StyledLink } from "@/components/StyledLink";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Container,
  useTheme,
  useMediaQuery,
  ListItemButton,
  Stack,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Outlet, useNavigate } from "react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext";
import { apiRemote } from "@/api";

const DRAWER_WIDTH = 280;

const STATUS_DRAFT = process.env.STATUS_DRAFT;
const STATUS_SUBMITTED = process.env.STATUS_SUBMITTED;
const STATUS_NOT_STARTED = process.env.STATUS_NOT_STARTED;
const STATUS_IN_PROGRESS = process.env.STATUS_IN_PROGRESS;
const STATUS_ENDED = process.env.STATUS_ENDED;

export default function MyThoughtCoCreate() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { userCompetition, setUserCompetition, userRound, setUserRound, userRoundParts, setUserRoundParts, setAiUsage } = useThoughtCoCreate();

  const [loading, setLoading] = useState<boolean>(false);

  const loadUserRound = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiRemote.getUserRoundStatus();
      if (response?.competition) {
        setUserCompetition(response.competition);
      }
      if (response?.rounds) {
        setUserRound(response.rounds);
      }
      if (response?.round_parts) {
        setUserRoundParts(response.round_parts);
      }
      if (response?.ai_usage) {
        setAiUsage(response.ai_usage);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userRound.length <= 0) {
      loadUserRound();
    }
  }, [userRound, userRoundParts, userCompetition]);

  const timelineItems = [
    {
      title: "Round 1",
      date: "Apr 1, 2025",
    },
    {
      title: "Round 2",
      date: "May 1, 2025",
    },
    {
      title: "Summit",
      date: "Jun 15, 2025",
    },
  ];

  const nextRoundPart = useMemo(() => {
    if (!userRoundParts || userRoundParts.length === 0) return null;

    // Kiểm tra round có status = STATUS_DRAFT hoặc STATUS_SUBMITTED
    const inProgressRoundPart = userRoundParts.find(
      (r) =>
        r.status === Number(STATUS_DRAFT) ||
        r.status === Number(STATUS_SUBMITTED)
    );
    if (inProgressRoundPart) return inProgressRoundPart;

    // Nếu không có, lấy round có round_id nhỏ nhất
    return userRoundParts.reduce((minRound, currentRound) =>
      currentRound.part_id < minRound.part_id ? currentRound : minRound
    );
  }, [userRoundParts]);

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "white",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        flex: 1,
        height: "100%",
      }}
    >
      {loading ? (
        <Box
          sx={{
            p: 3,
            bgcolor: "white",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: "background.default",
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  fontSize: { xs: "2rem", md: "3.75rem" },
                }}
              >
                {userCompetition?.name}
              </Typography>
              <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                {userCompetition?.short_desc}
              </Typography>

              {/* Timeline */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 4 }}
              >
                {/* {timelineItems.map((item, index) => (
                  <Paper
                    key={item.title}
                    sx={{
                      p: 2,
                      bgcolor: "primary.50",
                      minWidth: { sm: 150 },
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.date}
                    </Typography>
                  </Paper>
                ))} */}
                {userRound?.map((item: any) => (
                  <Paper
                    key={item.round_name}
                    sx={{
                      p: 2,
                      bgcolor: "primary.50",
                      minWidth: { sm: 150 },
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.round_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(item.due_date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </Typography>
                  </Paper>
                ))}
              </Stack>

              {/* Nút Start / Continue Submission */}
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: "black",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  "&:hover": { bgcolor: "grey.900" },
                }}
                disabled={!nextRoundPart}
              >
                <StyledLink
                  to={
                    nextRoundPart
                      ? `/${process.env.PREFIX}/my-thoughtcocreate/deliverable/${nextRoundPart.part_id}`
                      : "#"
                  }
                  style={{ color: "#fff" }}
                >
                  {nextRoundPart?.status === Number(STATUS_DRAFT) ||
                  nextRoundPart?.status === Number(STATUS_SUBMITTED)
                    ? "Continue Submissions"
                    : "Start Submission"}
                </StyledLink>
              </Button>
            </Box>

            <Box sx={{ maxWidth: 800, mx: "auto" }}>
              <Typography paragraph>
                {userCompetition?.description}
              </Typography>

              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: "bold",
                  my: 3,
                }}
              >
                Deliverables:
              </Typography>

              <List>
                {userRoundParts?.map((item: any) => (
                  <ListItem key={item.part_id} sx={{ px: 0 }}>
                    <StyledLink to={`./deliverable/${item.part_number}`}>
                      <ListItemText
                        primary={`D${item.part_number}: ${item.part_name}`}
                      />
                    </StyledLink>
                  </ListItem>
                ))}
              </List>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 4 }}
              >
                <Button variant="contained" sx={{ flex: { sm: 1 } }}>
                  <StyledLink to={`dashboard`} style={{ color: "#fff" }}>
                    View Submission Dashboard
                  </StyledLink>
                </Button>
                <Button variant="outlined" sx={{ flex: { sm: 1 } }} disabled>
                  Peer Voting
                </Button>
              </Stack>
            </Box>
          </Container>
        </Box>
      )}
    </Box>
  );
}
